import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FullscreenUtilService } from '@serviceplan/ui-kit';
import { CreateAssetDto } from '@thehubz/client/data-access-api';
import { Subscription } from 'rxjs';
import { AssetAnnotatorService } from '../../services/asset-annotator.service';

@Component({
  selector: 'thehubz-video-editor',
  templateUrl: './video-editor.component.html',
  styleUrls: ['./video-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule],
})
export class VideoEditorComponent implements OnInit, OnDestroy {
  @Input() fileUrl = '';
  @Output() openFullScreenClick = new EventEmitter<void>();
  private sceneWidth = 0;
  private sceneHeight = 0;
  private assetTypes = CreateAssetDto.AssetTypeEnum;
  isVideoPlaying = false;
  isVideoFullscreen = false;
  skipVideo = 0;
  playBackSpeed = 1;
  volumeIcon = 'volume_up';
  pausedTime = 0;
  @ViewChild('videoRef', { read: ElementRef }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('progressBar', { read: ElementRef }) progressBar!: ElementRef;
  @ViewChild('seek', { read: ElementRef }) seek!: ElementRef;
  @ViewChild('timeElapsed', { read: ElementRef }) timeElapsed!: ElementRef;
  @ViewChild('duration', { read: ElementRef }) duration!: ElementRef;
  @ViewChild('seekTooltip', { read: ElementRef }) seekTooltip!: ElementRef;
  @ViewChild('volumeButton', { read: ElementRef }) volumeButton!: ElementRef;
  @ViewChild('volume', { read: ElementRef }) volume!: ElementRef;
  @ViewChild('fullscreenButton', { read: ElementRef }) fullscreenButton!: ElementRef;

  /**
   * Store rxjs subscriptions to unsubscribe on destroy
   */
  private _subs = new Subscription();

  constructor(private readonly anService: AssetAnnotatorService, private readonly fullscreenUtil: FullscreenUtilService) {}

  ngOnInit(): void {
    // subscribe to fullscreen change event
    this.handleFullscreenChange();
  }

  /**
   * Listen to fullscreen change event and update the annotation element
   */
  private handleFullscreenChange() {
    this._subs.add(
      this.fullscreenUtil.fullscreenChange$.subscribe((isFullscreen) => {
        // update annotation element size
        // also, recreate the konva stage
        this._resetAnnotationElement(isFullscreen);
      }),
    );
  }

  // initializeVideo sets the video duration, and maximum value of the
  // progressBar
  initializeVideo() {
    const videoDuration = this.videoRef.nativeElement.duration;
    this.seek.nativeElement.setAttribute('max', videoDuration);
    this.progressBar.nativeElement.setAttribute('max', videoDuration);
    const time = this.formatTime(videoDuration);
    this.duration.nativeElement.innerText = `${time.minutes}:${time.seconds}`;
    this.duration.nativeElement.setAttribute('datetime', `${time.minutes}m ${time.seconds}s`);

    this.sceneHeight = this.videoRef.nativeElement.clientHeight;
    this.sceneWidth = this.videoRef.nativeElement.clientWidth;

    this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
  }

  draw(tool: string) {
    const scrollLeft = document.getElementById('annotation-window')?.scrollLeft || 0;
    const scrollTop = document.getElementById('annotation-window')?.scrollTop || 0;
    this.anService.drawTool(tool, this.assetTypes.Video, scrollLeft, scrollTop);
  }

  gotoTimeframe(timestamp: number) {
    if (this.isVideoPlaying) {
      this.videoRef.nativeElement.pause();
      this.isVideoPlaying = false;
    }
    this.videoRef.nativeElement.currentTime = timestamp;
    this.progressBar.nativeElement.value = timestamp;
    this.seek.nativeElement.value = timestamp;
    this.pausedTime = timestamp;
    this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
  }

  // video methods
  jumpVideo(skipTime: number, direction: string) {
    if (direction === 'backward') this.skipVideo -= skipTime;
    if (direction === 'forward') this.skipVideo += skipTime;
    this.videoRef.nativeElement.currentTime = this.skipVideo;
    //if video is not playing than render canvas and show annotations
    if (!this.isVideoPlaying) {
      this.anService.destroyStage();
      this.pausedTime = Math.floor(this.videoRef.nativeElement.currentTime);
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
    }
  }

  resetVideoTime() {
    this.skipVideo = this.videoRef.nativeElement.currentTime;
  }

  videoEnded() {
    this.isVideoPlaying = false;
  }

  playVideo() {
    if (this.videoRef.nativeElement.paused || this.videoRef.nativeElement.ended) {
      this.videoRef.nativeElement.play();
      this.isVideoPlaying = true;
      this.anService.destroyStage();
    } else {
      this.isVideoPlaying = false;
      this.videoRef.nativeElement.pause();
      this.pausedTime = Math.floor(this.videoRef.nativeElement.currentTime);
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
    }
  }

  setPlaybackSpeed(direction: number) {
    if (direction === 1 && this.videoRef.nativeElement.playbackRate < 2) this.videoRef.nativeElement.playbackRate += 0.25;
    if (direction === -1 && this.videoRef.nativeElement.playbackRate > 0.25) this.videoRef.nativeElement.playbackRate -= 0.25;
    this.playBackSpeed = this.videoRef.nativeElement.playbackRate;
  }

  // updateTimeElapsed indicates how far through the video
  // the current playback is
  updateTimeElapsed() {
    const time = this.formatTime(this.videoRef.nativeElement.currentTime);
    this.timeElapsed.nativeElement.innerText = `${time.minutes}:${time.seconds}`;
    this.timeElapsed.nativeElement.setAttribute('datetime', `${time.minutes}m ${time.seconds}s`);
  }
  // updateProgress indicates how far through the video
  // the current playback is by updating the progress bar
  updateProgress() {
    this.seek.nativeElement.value = this.videoRef.nativeElement.currentTime;
    this.progressBar.nativeElement.value = this.videoRef.nativeElement.currentTime;
  }
  // updateSeekTooltip uses the position of the mouse on the progress bar to
  // roughly work out what point in the video the user will skip to if
  // the progress bar is clicked at that point
  updateSeekTooltip(event: any) {
    const skipTo = Math.round((event.offsetX / event.target.clientWidth) * parseInt(event.target.getAttribute('max'), 10));
    this.seek.nativeElement.setAttribute('data-seek', skipTo);
    const t = this.formatTime(skipTo);
    this.seekTooltip.nativeElement.textContent = `${t.minutes}:${t.seconds}`;
    const rect = this.videoRef.nativeElement.getBoundingClientRect();
    this.seekTooltip.nativeElement.style.left = `${event.pageX - rect.left}px`;
  }
  // skipAhead jumps to a different point in the video when
  // the progress bar is clicked
  skipAhead(event: any) {
    const skipTo = event.target.dataset.seek ? event.target.dataset.seek : event.target.value;
    this.videoRef.nativeElement.currentTime = skipTo;
    this.progressBar.nativeElement.value = skipTo;
    this.seek.nativeElement.value = skipTo;

    if (!this.isVideoPlaying) {
      this.pausedTime = Math.floor(this.videoRef.nativeElement.currentTime);
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
    }
  }
  // updateVolume updates the video's volume
  // and disables the muted state if active
  updateVolume() {
    if (this.videoRef.nativeElement.muted) {
      this.videoRef.nativeElement.muted = false;
    }
    this.videoRef.nativeElement.volume = this.volume.nativeElement.value;
  }
  // updateVolumeIcon updates the volume icon so that it correctly reflects
  // the volume of the video
  updateVolumeIcon() {
    if (this.videoRef.nativeElement.volume >= 0.5) {
      this.volumeIcon = 'volume_up';
    } else if (this.videoRef.nativeElement.volume < 0.5 && this.videoRef.nativeElement.volume > 0) {
      this.volumeIcon = 'volume_down';
    } else {
      this.volumeIcon = 'volume_mute';
    }
  }
  // formatTime takes a time length in seconds and returns the time in
  // minutes and seconds
  formatTime(timeInSeconds: number) {
    const result = new Date(timeInSeconds * 1000).toISOString().substring(14, 19);
    return {
      minutes: result.substring(0, 2),
      seconds: result.substring(3),
    };
  }
  // toggleMute mutes or unmutes the video when executed
  // When the video is unmuted, the volume is returned to the value
  // it was set to before the video was muted
  toggleMute() {
    this.videoRef.nativeElement.muted = !this.videoRef.nativeElement.muted;
    if (this.videoRef.nativeElement.muted) {
      this.volume.nativeElement.setAttribute('data-volume', this.volume.nativeElement.value);
      this.volume.nativeElement.value = 0;
      this.videoRef.nativeElement.volume = 0;
    } else {
      this.volume.nativeElement.value = this.volume.nativeElement.dataset.volume;
      this.videoRef.nativeElement.volume = this.volume.nativeElement.value;
    }
  }
  // toggleFullScreen toggles the full screen state of the video
  // If the browser is currently in fullscreen mode,
  // then it should exit and vice versa.
  toggleFullScreen() {
    this.isVideoFullscreen = !this.isVideoFullscreen;
    this.openFullScreenClick.emit();
  }

  /**
   * Set annotation window height
   *
   */
  private _resetAnnotationElement(isFullscreen: boolean) {
    const annotationWindow = document.getElementById('annotation-window');
    if (isFullscreen) {
      if (annotationWindow) {
        this.anService.destroyStage();
        annotationWindow.style.height = '94%';
      }
    } else {
      if (annotationWindow) {
        annotationWindow.style.height = '50vh';
        this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Video, 0, this.pausedTime);
      }
    }
  }

  ngOnDestroy(): void {
    // unsubscribe observables
    this._subs.unsubscribe();
  }
}
